import { useState } from 'react'
import styled from 'styled-components'
import isEmpty from 'lodash/isEmpty'
import { type UniversalCategory } from '@/features/shop/services/UniversalCategories/types'
import { useMarketingZip } from '@/features/shop/components/UngatedCategoryPage/hooks'
import {
  urlBuilder,
  getUniversalCategories,
} from '@/features/shop/services/UniversalCategories/utils'
import { Button } from '@shipt/design-system-buttons'
import { ButtonLink } from '@/features/shared/elements/Link/Buttons'
import { Body } from '@shipt/design-system-typography'
import { screenSizes } from '@shipt/design-system-themes'

type Props = {
  category: UniversalCategory
}

export const CategoryTabGroup = ({ category }: Props) => {
  const zip = useMarketingZip()

  // Filter out any duplicate children categories based on the child's category id
  const categories =
    category.children?.filter(
      (cat, index, self) => index === self.findIndex((c) => c.id === cat.id),
    ) || []
  const universalCategories = getUniversalCategories()

  const truncateLimit = 10
  const shouldTruncate = Boolean(Number(categories.length) > truncateLimit)
  const [isTruncated, setIsTruncated] = useState(shouldTruncate)

  if (isEmpty(categories)) return null

  return (
    <>
      {categories.slice(0, isTruncated ? truncateLimit : undefined).map((_category) => {
        const { id, name } = _category
        return (
          <CategoryLink
            key={id}
            href={urlBuilder({
              universalCategories,
              category: _category,
              zip,
            })}
            shallow
            size="sm"
            variant="tertiary"
          >
            <Body strong size="md">
              {name}
            </Body>
          </CategoryLink>
        )
      })}
      {!!shouldTruncate && (
        <TruncatedButton onClick={() => setIsTruncated(!isTruncated)} size="sm" variant="tertiary">
          View {isTruncated ? 'more' : 'less'}
        </TruncatedButton>
      )}
    </>
  )
}

const TruncatedButton = styled(Button)`
  flex-shrink: 0;
`

const CategoryLink = styled(ButtonLink)`
  flex-shrink: 0;
  margin-right: 0.5rem;
  height: 1.75rem;
  white-space: nowrap;
  place-self: flex-end;

  @media ${screenSizes.tablet} {
    height: 2.25rem;
    margin: 0.5rem 0.5rem 0.5rem 0;
  }
`
